import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useManagerTodayData } from '../../../hooks/useManagerTodayData';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import TopActionsSection from '../../../components/manager-v2/TopActionsSection';

/**
 * Manager V2 Actions — Liste complète des actions à mener sur l'équipe.
 * Route : /manager/actions + /manager/v2/actions.
 * Feature : pages.manager.actions.
 *
 * Réutilise `useManagerTodayData` (calcul onboarding/inactive cleartext, no scraping).
 * Affiche la totalité (pas seulement le top 5 comme sur le dashboard Today).
 */
const KIND_FILTERS = ['all', 'onboarding', 'inactive', 'risk'];

const ManagerV2Actions = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [filter, setFilter] = useState('all');
  const { actions, criticalCount, loading, error, reload } = useManagerTodayData();

  const filtered = filter === 'all' ? actions : actions.filter((a) => a.kind === filter);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                {t('actionsV2.title', 'Actions')}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {tenantName} · {actions.length} {t('actionsV2.pending', 'pending')}
              </p>
            </div>
            <div className="inline-flex rounded-lg overflow-hidden border border-border bg-card">
              {KIND_FILTERS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFilter(k)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    filter === k ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t(`actionsV2.filter.${k}`, k)}
                </button>
              ))}
            </div>
          </div>
        </header>
        <div className="p-4 md:p-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between mb-4">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}
          <TopActionsSection actions={filtered} criticalCount={criticalCount} loading={loading} limit={500} showSeeAll={false} />
        </div>
      </main>
    </div>
  );
};

export default ManagerV2Actions;
